import React, { useState, useMemo, useEffect } from "react";
import { Gauge, TrendingUp, Activity, Target } from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ReferenceLine,
  ReferenceArea,
} from "recharts";

const PerformanceCharts = ({ player, isDarkMode }) => {
  const [activeChart, setActiveChart] = useState("trainability");
  
  const lastSession = player?.sessions?.[player.sessions.length - 1];
  const accelData = lastSession?.session_acceleration_sprint;
  const activationData = lastSession?.session_activation_repartition;

  // Debug force-velocity data structure
  useEffect(() => {
    if (lastSession?.session_performance) {
  
    }
  }, [lastSession]);

  // Prepare sprint distribution data (first 15 bins for clarity).
  // sprint_number is a scalar total in the current format; only legacy rows
  // stored a per-bin array — keep plotting per-bin counts in that case only.
  const sprintChartData = useMemo(() => {
    if (!accelData?.velocity) return [];
    const sprintNumberArray = Array.isArray(accelData?.sprint_number) ? accelData.sprint_number : null;
    if (!sprintNumberArray && !accelData?.sprint_distance) return [];
    return accelData.velocity.slice(0, 15).map((v, i) => ({
      velocity: Math.round(v * 3.6), // Convert to km/h display
      count: sprintNumberArray?.[i] || 0,
      distance: Math.round((accelData.sprint_distance?.[i] || 0) * 10) / 10,
    }));
  }, [accelData]);

  // Prepare acceleration distribution data (first 30 bins)
  const accelChartData = useMemo(() => {
    if (!accelData?.acceleration || !accelData?.acceleration_number) return [];
    return accelData.acceleration.slice(0, 30).map((a, i) => ({
      accel: Math.round(a * 10) / 10,
      count: accelData.acceleration_number[i] || 0,
      decel: accelData.deceleration_number?.[i] || 0,
    }));
  }, [accelData]);

  // Prepare trainability evolution data — aligned with TrainerTeamPerformanceCharts
  const trainabilityEvolutionData = useMemo(() => {
    // Use weeklyHistory if available (from team charts format), otherwise fallback to sessions
    const wh = player?.weeklyHistory;
    const dataSource = (Array.isArray(wh) && wh.length > 0) ? wh : (player?.sessions || []);
    if (dataSource.length === 0) return [];

    // Convert trainability to /10 scale helper (same as team charts)
    const toScore10 = (value) => {
      const n = Number(value || 0);
      if (!Number.isFinite(n)) return 0;
      if (n >= 0 && n <= 1.5) return Math.max(0, Math.min(10, n * 10));
      if (n > 10 && n <= 100) return Math.max(0, Math.min(10, n / 10));
      if (n > 100 && n <= 10000) return Math.max(0, Math.min(10, n / 1000));
      return Math.max(0, Math.min(10, n));
    };

    const parseDate = (dateStr) => {
      if (!dateStr) return new Date(0);
      if (dateStr.includes('/')) {
        const [d, m, y] = dateStr.split('/');
        return new Date(`${y}-${m}-${d}`);
      }
      return new Date(dateStr);
    };

    // Group by day to create data points
    const dayMap = new Map();

    dataSource.forEach((session) => {
      // Support both formats: weeklyHistory (day, phase) and sessions (date, simulation_origin)
      let day = session.day;
      if (!day) {
        const dateStr = session.date?.split('T')[0];
        if (dateStr) {
          const [year, month, dayNum] = dateStr.split('-');
          day = `${dayNum}/${month}/${year}`;
        }
      }
      if (!day) return;

      // Determine if predictive
      let isPredictive = false;
      if (session.phase !== undefined) {
        isPredictive = session.phase === "predictive";
      } else if (session.simulation_origin !== undefined) {
        isPredictive = (session.simulation_origin || "").startsWith("scenario_");
      }

      const trainabilityValue = session.trainability ?? session.trainability_index ?? 0;
      const roundedScore = Math.round(toScore10(trainabilityValue) * 10) / 10;

      if (!dayMap.has(day)) {
        dayMap.set(day, {
          day,
          trainability_real: null,
          trainability_pred: null,
          _isPredictive: false,
        });
      }

      const point = dayMap.get(day);
      if (isPredictive) {
        point.trainability_pred = roundedScore;
        point._isPredictive = true;
      } else {
        point.trainability_real = roundedScore;
      }
    });

    // Convert to array and sort chronologically
    const result = Array.from(dayMap.values()).sort(
      (a, b) => parseDate(a.day) - parseDate(b.day)
    );

    // Insert a bridge point so real & predictive lines connect visually
    for (let i = 1; i < result.length; i++) {
      const prev = result[i - 1];
      const curr = result[i];
      if (prev.trainability_real !== null && !prev._isPredictive &&
          curr.trainability_pred !== null && curr._isPredictive) {
        // Copy last real value as the start of predictive line
        curr.trainability_pred_bridge = prev.trainability_real;
      }
    }
    // Merge bridge into pred series for continuity
    result.forEach((pt) => {
      if (pt.trainability_pred_bridge !== undefined && pt.trainability_pred === null) {
        pt.trainability_pred = pt.trainability_pred_bridge;
      }
    });
    // Also set bridge on the last real point
    for (let i = 0; i < result.length - 1; i++) {
      const curr = result[i];
      const next = result[i + 1];
      if (curr.trainability_real !== null && !curr._isPredictive &&
          next._isPredictive) {
        curr.trainability_pred = curr.trainability_real;
      }
    }

    return result;
  }, [player]);

  // Determine the first predictive date and last date for reference area
  const predictiveBoundaryDate = useMemo(() => {
    const pred = trainabilityEvolutionData
      .filter(p => p._isPredictive)
      .map(p => p.day);
    return pred.length ? pred[0] : null;
  }, [trainabilityEvolutionData]);

  const lastTrainabilityDate = useMemo(() => {
    const days = trainabilityEvolutionData.map(p => p.day);
    return days.length ? days[days.length - 1] : null;
  }, [trainabilityEvolutionData]);

  // Prepare muscle intensity radar data
  const muscleRadarData = useMemo(() => {
    if (!activationData) return [];
    const muscles = ["quadriceps", "hamstring", "adductors", "calf", "gluteal", "iliopsoas"];
    return muscles.map((m) => ({
      muscle: m.charAt(0).toUpperCase() + m.slice(1, 4),
      left: Math.round((activationData[m]?.left?.intensity || 0) * 100),
      right: Math.round((activationData[m]?.right?.intensity || 0) * 100),
    }));
  }, [activationData]);

  // Early return AFTER all hooks — allow rendering if at least trainability data exists
  const hasTrainabilityData = trainabilityEvolutionData.length > 0;
  if (!player) return null;
  if (!player.sessions?.length && !hasTrainabilityData) return null;
  if (!accelData && !activationData && !hasTrainabilityData) return null;

  const chartTabs = [
    { key: "trainability", label: "Trainability", icon: Activity },
    { key: "muscle", label: "Muscles", icon: Target },
  ];

  const chartColors = isDarkMode 
    ? { primary: "#34d399", secondary: "#f472b6", tertiary: "#60a5fa", grid: "#374151", text: "#9ca3af" }
    : { primary: "#10b981", secondary: "#ec4899", tertiary: "#3b82f6", grid: "#e5e7eb", text: "#6b7280" };

  return (
    <div className="mt-6">
      {/* Chart tabs - minimal */}
      <div className="flex items-center gap-1 mb-4">
        {chartTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeChart === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveChart(tab.key)}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                ${isActive
                  ? isDarkMode ? "bg-gray-700 text-white" : "bg-gray-900 text-white"
                  : isDarkMode ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"
                }
              `}
            >
              <Icon size={12} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Charts */}
      <div className={`h-48 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
        {activeChart === "sprint" && sprintChartData.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sprintChartData}>
              <defs>
                <linearGradient id="sprintGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={chartColors.primary} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={chartColors.primary} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
              <XAxis 
                dataKey="velocity" 
                tick={{ fontSize: 10, fill: chartColors.text }}
                axisLine={{ stroke: chartColors.grid }}
                tickLine={false}
                label={{ value: "km/h", position: "insideBottomRight", offset: -5, fontSize: 10, fill: chartColors.text }}
              />
              <YAxis 
                tick={{ fontSize: 10, fill: chartColors.text }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: isDarkMode ? "#1f2937" : "#fff",
                  border: "none",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  fontSize: "11px"
                }}
                labelFormatter={(v) => `${v} km/h`}
              />
              <Area 
                type="monotone" 
                dataKey="count" 
                stroke={chartColors.primary} 
                fill="url(#sprintGradient)"
                strokeWidth={2}
                name="Occurrences"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}

        {activeChart === "accel" && accelChartData.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={accelChartData} barGap={0}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
              <XAxis 
                dataKey="accel" 
                tick={{ fontSize: 10, fill: chartColors.text }}
                axisLine={{ stroke: chartColors.grid }}
                tickLine={false}
              />
              <YAxis 
                tick={{ fontSize: 10, fill: chartColors.text }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: isDarkMode ? "#1f2937" : "#fff",
                  border: "none",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  fontSize: "11px"
                }}
              />
              <Bar dataKey="count" fill={chartColors.primary} name="Accel." radius={[2, 2, 0, 0]} />
              <Bar dataKey="decel" fill={chartColors.secondary} name="Decel." radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}

        {activeChart === "trainability" && trainabilityEvolutionData.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trainabilityEvolutionData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
              <XAxis 
                dataKey="day" 
                tick={{ fontSize: 9, fill: chartColors.text }}
                axisLine={{ stroke: chartColors.grid }}
                tickLine={false}
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
                tickFormatter={(v) => {
                  if (!v) return "";
                  const [d, m] = v.split("/");
                  return `${d}/${m}`;
                }}
              />
              <YAxis 
                tick={{ fontSize: 10, fill: chartColors.text }}
                axisLine={false}
                tickLine={false}
                domain={[0, 10]}
                tickFormatter={(v) => v != null ? v.toFixed(1) : ""}
                label={{ value: "/10", angle: -90, position: "insideLeft", fontSize: 9, fill: chartColors.text }}
              />

              {/* Predictive zone background */}
              {predictiveBoundaryDate && lastTrainabilityDate && (
                <ReferenceArea
                  x1={predictiveBoundaryDate}
                  x2={lastTrainabilityDate}
                  fill={isDarkMode ? "rgba(251,191,36,0.08)" : "rgba(245,158,11,0.08)"}
                  fillOpacity={0.5}
                  ifOverflow="extendDomain"
                />
              )}
              {/* Predictive boundary line */}
              {predictiveBoundaryDate && (
                <ReferenceLine
                  x={predictiveBoundaryDate}
                  stroke={isDarkMode ? "rgba(251,191,36,0.9)" : "rgba(245,158,11,0.9)"}
                  strokeDasharray="4 4"
                  ifOverflow="extendDomain"
                  label={{
                    value: "Pred.",
                    position: "insideTopRight",
                    fill: isDarkMode ? "rgba(251,191,36,0.95)" : "rgba(180,83,9,0.95)",
                    fontSize: 9,
                    fontWeight: 600,
                  }}
                />
              )}

              <Tooltip 
                content={({ active, payload }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const data = payload[0].payload;
                  const realValue = data.trainability_real;
                  const predValue = data.trainability_pred;
                  const isPred = data._isPredictive;
                  
                  return (
                    <div style={{
                      backgroundColor: isDarkMode ? "#1f2937" : "#fff",
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      fontSize: "11px",
                      color: isDarkMode ? "#e5e7eb" : "#1f2937",
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                        <span style={{ fontWeight: 600 }}>{data.day}</span>
                        {isPred && (
                          <span style={{
                            fontSize: "9px", background: "#f59e0b", color: "#fff",
                            borderRadius: "4px", padding: "1px 5px", fontWeight: 600,
                          }}>Pred.</span>
                        )}
                      </div>
                      {realValue !== null && !isPred && (
                        <p style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px" }}>
                          <span style={{ color: chartColors.tertiary }}>●</span>
                          <span>Historical: <b>{realValue}/10</b></span>
                        </p>
                      )}
                      {predValue !== null && isPred && (
                        <p style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{ color: "#f59e0b" }}>●</span>
                          <span>Predictive: <b>{predValue}/10</b></span>
                        </p>
                      )}
                    </div>
                  );
                }}
              />

              {/* Historical (real) line — solid */}
              <Line 
                type="monotone" 
                dataKey="trainability_real" 
                stroke={chartColors.tertiary}
                strokeWidth={2}
                dot={{ r: 2.5, stroke: chartColors.tertiary, fill: isDarkMode ? "#111827" : "#ffffff" }}
                activeDot={{ r: 4 }}
                connectNulls={false}
                isAnimationActive={false}
                name="Historical"
              />
              {/* Predictive line — dashed */}
              <Line 
                type="monotone" 
                dataKey="trainability_pred" 
                stroke="#f59e0b"
                strokeDasharray="6 4"
                strokeWidth={2.5}
                dot={{ r: 2.5, stroke: "#f59e0b", fill: isDarkMode ? "#111827" : "#ffffff" }}
                activeDot={{ r: 4 }}
                connectNulls={false}
                isAnimationActive={false}
                name="Predictive"
              />
            </LineChart>
          </ResponsiveContainer>
        )}

        {activeChart === "muscle" && muscleRadarData.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={muscleRadarData}>
              <PolarGrid stroke={chartColors.grid} />
              <PolarAngleAxis 
                dataKey="muscle" 
                tick={{ fontSize: 9, fill: chartColors.text }}
              />
              <PolarRadiusAxis 
                tick={{ fontSize: 8, fill: chartColors.text }}
                axisLine={false}
                domain={[0, 60]}
              />
              <Radar 
                name="Left" 
                dataKey="left" 
                stroke={chartColors.primary} 
                fill={chartColors.primary} 
                fillOpacity={0.3}
                strokeWidth={2}
              />
              <Radar 
                name="Right" 
                dataKey="right" 
                stroke={chartColors.tertiary} 
                fill={chartColors.tertiary} 
                fillOpacity={0.3}
                strokeWidth={2}
              />
              <Legend 
                wrapperStyle={{ fontSize: "10px" }}
              />
            </RadarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default PerformanceCharts;
